import type { ReactNode } from 'react';
import { Text } from '@backstage/ui';

/**
 * One row of a settings card (mockups « F1 », « R » and « U1 »,
 * 2026-10-16): its label and what it says under it on the left, its control
 * on the right (a boxed input, a segmented control, a switch, or a value), an
 * action after it (a lock, « Modifier »), the reference's value (« preprod :
 * 3 », in the reference tier's ink) and its problems last.
 *
 * `setting`: label left, control right — the « Configuration » tab.
 * `summary`: a fixed label column, the value taking the rest, the action at
 * the end — the review step of the creation form.
 *
 * A `group` named by its label, so a screen reader announces it on entering
 * the control.
 */
export function SettingRow(props: {
  label: ReactNode;
  labelId: string;
  children: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  reference?: ReactNode;
  problems?: readonly string[];
  variant?: 'setting' | 'summary';
}) {
  const { problems = [] } = props;
  return (
    <div
      className="waadoo-setting-row"
      data-variant={props.variant ?? 'setting'}
      role="group"
      aria-labelledby={props.labelId}
    >
      <div className="waadoo-setting-row__head">
        <span className="waadoo-setting-row__label" id={props.labelId}>
          {props.label}
        </span>
        {props.hint}
      </div>
      <div className="waadoo-setting-row__control">{props.children}</div>
      {props.action && (
        <div className="waadoo-setting-row__action">{props.action}</div>
      )}
      {props.reference && (
        <div className="waadoo-setting-row__reference">{props.reference}</div>
      )}
      {problems.length > 0 && (
        <div className="waadoo-setting-row__problems">
          {problems.map(problem => (
            <Text
              key={problem}
              as="p"
              variant="body-small"
              color="danger"
              role="alert"
            >
              {problem}
            </Text>
          ))}
        </div>
      )}
    </div>
  );
}
